import * as React from 'react';

import { BrandIcon } from '@/components/ui/brand-icon';
import { cropGlyph } from '@/lib/cropGlyph';
import { cn } from '@/lib/utils';

/**
 * CropChip — единый способ показать культуру: фирменная иконка (если для
 * культуры есть глиф) + её имя. ADR-044: культура везде рендерится одним
 * чипом. Компонент feature-агностичный и цвето-нейтральный — он НЕ задаёт фон
 * (иконка красится `currentColor`, наследует цвет текста родителя), поэтому
 * встраивается в любой контейнер (таблицу, цветную плашку севооборота,
 * ячейку списка полей) без конфликтов с его оформлением.
 *
 * Иконки нет (`cropGlyph` вернул null) → рендерится только имя, без пустого
 * отступа. Пустое имя → `emptyLabel` (по умолчанию ничего).
 */
export interface CropChipProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Имя культуры (может быть «Пшеница озимая мягкая», display_name и т.п.). */
  name?: string | null;
  /** Размер иконки в px. По умолчанию 16. */
  iconSize?: number;
  /**
   * Иконка-заглушка, когда для культуры нет фирменного глифа (например
   * lucide `<Sprout/>`). По умолчанию иконка не показывается.
   */
  fallbackIcon?: React.ReactNode;
  /** Что показать, если имя пустое. По умолчанию — ничего. */
  emptyLabel?: React.ReactNode;
}

export const CropChip = React.forwardRef<HTMLSpanElement, CropChipProps>(
  ({ name, iconSize = 16, fallbackIcon, emptyLabel, className, ...props }, ref) => {
    const trimmed = name?.trim() ? name.trim() : null;
    const glyph = cropGlyph(trimmed);

    return (
      <span
        ref={ref}
        className={cn('inline-flex items-center gap-1.5 min-w-0', className)}
        {...props}
      >
        {glyph ? (
          <BrandIcon name={`crop-${glyph}`} size={iconSize} />
        ) : (
          fallbackIcon ?? null
        )}
        {trimmed ? <span className="truncate">{trimmed}</span> : emptyLabel}
      </span>
    );
  },
);
CropChip.displayName = 'CropChip';
